import React, { useCallback, useMemo, useState } from "react";
import {
  differenceInMinutes,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import { Maybe } from "true-myth";

import { formatDateRange } from "helpers/times";
import { ILocation } from "types/account";
import { IShift, IPerson, IPosUser } from "types/schedules";

import { OpenBlockFunc } from "actions/scheduling/blocks";
import BlockHoverMask from "components/schedule/block_hover_mask";
import CategoryFlag, { IFlagPosition } from "components/schedule/category_flag";
import {
  StyledTimeBlock,
  StyledRole,
  StyledTime,
  BORDERS,
  StyledTotalHours,
} from "components/schedule/time_block";
import { trackEvent } from "helpers/analytics";
import { useShiftTotalHoursHandler } from "../../hooks/schedule/useShiftTotalHoursHandler";

interface IShiftButton {
  border?: BORDERS;
  date: Date;
  deleteShift: (shift: IShift, assignee: Maybe<IPosUser>) => void;
  interactiveView: boolean;
  openBlockModal: OpenBlockFunc;
  person: IPerson;
  partialFlag: boolean;
  shift: IShift;
  storeLocation: Maybe<ILocation>;
}

const MINUTES_IN_DAY = 24 * 60;
function minutesToPositionPercent(minutes: number) {
  // x100 because in percentage
  // max(0) to remove negatives
  return Math.round((100 * Math.max(0, minutes)) / MINUTES_IN_DAY);
}

function ShiftButton({
  border,
  date,
  deleteShift,
  interactiveView,
  openBlockModal,
  partialFlag,
  person,
  shift,
  storeLocation,
}: IShiftButton) {
  // Default flag position fills the whole width
  let flagPosition: IFlagPosition = {
    left: 0,
    right: 0,
  };
  if (partialFlag) {
    // Look for store location trading day and show a partial category flag to match shift time
    const dayStart = storeLocation.match({
      Just: (location) =>
        setHours(
          setMinutes(shift.startTime, location.tradingDayCloseMinute),
          location.tradingDayCloseHour
        ),
      Nothing: () => startOfDay(shift.startTime),
    });
    const minutesBefore = differenceInMinutes(shift.startTime, dayStart);
    const shiftLength = differenceInMinutes(shift.endTime, shift.startTime);
    const minutesAfter = MINUTES_IN_DAY - minutesBefore - shiftLength;

    flagPosition = {
      left: minutesToPositionPercent(minutesBefore),
      right: minutesToPositionPercent(minutesAfter),
    };
  }

  const blockType = person.assignee.isJust()
    ? "assigned shift"
    : "unassigned shift";

  const onEdit = useCallback(() => {
    trackEvent("Time Block - Edit Icon - Click", { blockType });
    openBlockModal({
      existingBlock: Maybe.of(shift),
      person,
      date,
    });
  }, [shift, person, date, blockType]);
  const onDelete = useCallback(() => {
    trackEvent("Time Block - Delete Icon - Click", { blockType });
    deleteShift(shift, person.assignee);
  }, [shift, person, blockType]);
  const [isHovered, setHover] = useState(false);
  const hoverMask = isHovered ? (
    <BlockHoverMask onEdit={onEdit} onDelete={onDelete} />
  ) : undefined;
  const setHoverWhenInteractive = useCallback(
    (newVal) => () => {
      if (interactiveView) {
        if (newVal) {
          trackEvent("Time Block - Hover", { blockType });
        }
        setHover(newVal);
      }
    },
    [interactiveView, blockType]
  );

  const htmlRole = interactiveView ? "button" : undefined;
  const hoveredBorder =
    border === BORDERS.UNASSIGNED_DAY || border === BORDERS.SHIFT_DAY
      ? BORDERS.SIDES
      : BORDERS.FULL;

  const shiftTotalHoursHandler = useShiftTotalHoursHandler();
  const totalHours = useMemo(() => shiftTotalHoursHandler(shift), [shift]);

  return (
    <StyledTimeBlock
      border={isHovered ? hoveredBorder : border}
      role={htmlRole}
      onMouseEnter={setHoverWhenInteractive(true)}
      onMouseLeave={setHoverWhenInteractive(false)}
    >
      {!isHovered && (
        <CategoryFlag category={shift.role.category} position={flagPosition} />
      )}
      <StyledTime>{formatDateRange(shift.startTime, shift.endTime)}</StyledTime>
      <StyledRole>{shift.role.name}</StyledRole>
      <StyledTotalHours>Hours: {totalHours}</StyledTotalHours>
      {hoverMask}
    </StyledTimeBlock>
  );
}

export default ShiftButton;
